import { __rest } from "tslib";
import { siteType as siteTypes, CircularProductTypes, isHostnameContainsCC, fetchers, hasHttpOrHttps, ApigeeCircularStockAvailability, format } from '@pes-ui/utils';
import { getSprykerBaseQueryParams } from '../../../globals/utils/helpers';
import { getData } from '../../../globals/http';
import { isZipCodeGeolocated } from './zip-code';
import { applyPriceTemplate } from './price';
const RETAILER_PRODUCT_IN_STOCK = 'InStock';
export function getBuyOnlineAvailability(products, distributorsInfo) {
  return products.reduce((result, product) => {
    var _a;
    result[product.commercialReference] = Boolean((_a = distributorsInfo[product.commercialReference]) === null || _a === void 0 ? void 0 : _a.length);
    return result;
  }, {});
}
const getCircularProductStockAvailability = async (parameters) => {
  const { result, error } = await ApigeeCircularStockAvailability.getCircularStockAvailability(parameters);
  if (error) {
    console.error(`[PES]: Error fetching stock for ${parameters.queryParams.productCommercialReference}:`, error);
    return false;
  }
  if (!result || !result.availableQuantity) {
    return false;
  }
  return Number(result.availableQuantity) > 0;
};
export async function getCircularProductsStockAvailability(products, countryCode, apiEndPointUrl, apiKey) {
  const stockAvailability = {};
  const stockAvailabilityPromises = products.map((product) => getCircularProductStockAvailability(Object.assign({ url: apiEndPointUrl, queryParams: {
      country: countryCode,
      productCommercialReference: product.commercialReference,
    } }, (apiKey && { apiKey }))).then((isAvailableInStock) => {
    stockAvailability[product.commercialReference] = isAvailableInStock;
  }));
  await Promise.all(stockAvailabilityPromises);
  return stockAvailability;
}
export function getNotificationMessage(zipCode, distributorsLength, { options: { useZipCode, enableEmptyZipCode }, messages: { allDistributorsShown, autoDetectedZipCode }, }) {
  if (useZipCode && distributorsLength && isZipCodeGeolocated()) {
    return autoDetectedZipCode;
  }
  if (useZipCode && !zipCode && enableEmptyZipCode && distributorsLength) {
    return allDistributorsShown;
  }
  return '';
}
function getDistributors(productsDistributorsInfo, id, options) {
  if (productsDistributorsInfo && id in productsDistributorsInfo) {
    const distributors = productsDistributorsInfo[id];
    const { priceTemplate, priceLocale } = options;
    return distributors.map((_a) => {
      var { price } = _a, rest = __rest(_a, ["price"]);
      return Object.assign(Object.assign({}, rest), { price, priceFormatted: applyPriceTemplate(format(price, { locale: priceLocale }), priceTemplate) });
    });
  }
  return [];
}
export function getAllDistributors(id, productsDistributorsInfo, additionalProductsDistributorsInfo, options) {
  let distributors = getDistributors(productsDistributorsInfo, id, options);
  if (additionalProductsDistributorsInfo) {
    const additionalDistributors = getDistributors(additionalProductsDistributorsInfo, id, options).map((distributor) => (Object.assign(Object.assign({}, distributor), { showPrice: false })));
    distributors = [...additionalDistributors, ...distributors];
  }
  return distributors;
}
const isDIYRetailer = ({ Key, Value }) => Key === 'IsDIY' && Value === 'true';
const retailerShouldBeVisible = (metaData, siteType) => {
  if (siteType === siteTypes.B2B && Array.isArray(metaData) && metaData.some(isDIYRetailer)) {
    return false;
  }
  if (siteType === siteTypes.B2C && Array.isArray(metaData) && !metaData.some(isDIYRetailer)) {
    return false;
  }
  return true;
};
function getProductsDistributorsInfo({ products, showPrice, stockLabels, siteType, isCircularProduct, }) {
  return products.reduce((result, product) => {
    result[product.ProductId] = product.RetailerProducts.reduce((result, { RetailerId, RetailerName, RetailerLogoUrl, StockPrices, ClickThruUrl, RetailerCurrency, RetailerSite, RetailerMetaData, }) => {
      if (!retailerShouldBeVisible(RetailerMetaData, siteType)) {
        return result;
      }
      const defaultStockPrice = StockPrices && StockPrices.find((stockPriceItem) => stockPriceItem.IsDefault);
      const isInStock = defaultStockPrice.Stock === RETAILER_PRODUCT_IN_STOCK;
      const isAvailable = isCircularProduct || (defaultStockPrice ? isInStock : false);
      result.push(Object.assign(Object.assign({ id: RetailerId, name: RetailerName, logoUrl: RetailerLogoUrl, isAvailable, isFavorite: null, retailerSite: RetailerSite, buyUrl: ClickThruUrl, showPrice, stockLabel: isAvailable ? stockLabels.inStock : stockLabels.outOfStock }, (showPrice && defaultStockPrice && { price: defaultStockPrice.Price })), (showPrice && defaultStockPrice && { currency: RetailerCurrency })));
      return result;
    }, []);
    return result;
  }, {});
}
function getUrl({ feedUrl, zipCode, productIds, }) {
  const zipCodeParametr = zipCode ? `&cl=${zipCode}` : '';
  return `${feedUrl}${zipCodeParametr}&pid=${productIds}`;
}
export function getProductsDataFeed({ feedUrl, zipCode, productIds, }) {
  const url = getUrl({ feedUrl, zipCode, productIds });
  return getData(false, url).then((response) => response);
}
export function getProductIds(products) {
  return products.reduce((summ, product) => `${summ}${summ ? ',' : ''}${product.commercialReference}`, '');
}
export function splitStandardAndCircularProducts(products) {
  const standardProducts = [];
  const circularProducts = [];
  products.forEach((product) => {
    const productType = product.circularType || CircularProductTypes.LINEAR;
    if (productType === CircularProductTypes.LINEAR) {
      standardProducts.push(product);
    }
    else {
      circularProducts.push(product);
    }
  });
  return { standardProducts, circularProducts };
}
export function getFilteredProductIds(products) {
  const { standardProducts, circularProducts } = splitStandardAndCircularProducts(products);
  return {
    standardProductIds: standardProducts.map((product) => product.commercialReference).join(','),
    circularProductIds: circularProducts.map((product) => product.commercialReference).join(','),
  };
}
export function categorizeAndLinkProductsWithRelations(products) {
  const { standardProducts, circularProducts } = splitStandardAndCircularProducts(products);
  standardProducts.forEach((product) => {
    if (!product.circularProductRelationships || product.circularProductRelationships.length === 0) {
      return;
    }
    product.circularProductRelationships.forEach((relationship) => {
      if (relationship.circularType === CircularProductTypes.LINEAR) {
        return;
      }
      circularProducts.push({
        commercialReference: relationship.productId,
        circularType: relationship.circularType,
      });
    });
  });
  return { standardProducts, circularProducts };
}
export function normalizeZipCodePattern(pattern) {
  return new RegExp(pattern);
}
export function startSearch({ feedUrl, commercialReference, zipCode, stockLabels, showPrice, siteType, isCircularProduct, }) {
  return getProductsDataFeed({ feedUrl, zipCode, productIds: commercialReference }).then((productsDataFeed) => {
    const distributorProductsInfo = getProductsDistributorsInfo({
      products: productsDataFeed,
      showPrice,
      stockLabels,
      siteType,
      isCircularProduct,
    });
    return {
      distributorProductsInfo,
      productsDataFeed,
    };
  });
}
export function getCommerceConnectorSettings(apiHost, localeCode) {
  const isValidHost = hasHttpOrHttps(apiHost);
  if (isValidHost && !localeCode) {
    const parsedURL = new URL(apiHost);
    const path = parsedURL.pathname.endsWith('/')
      ? parsedURL.pathname.slice(0, -1)
      : parsedURL.pathname;
    const { contextRoot, countryCode, languageCode } = getSprykerBaseQueryParams(path);
    return fetchers.getCommerceConnector({
      host: parsedURL.origin,
      contextRoot,
      countryCode: !isHostnameContainsCC() ? countryCode : null,
      languageCode,
    });
  }
  const { contextRoot, countryCode, languageCode, } = getSprykerBaseQueryParams(localeCode || apiHost);
  return fetchers.getCommerceConnector({
    host: isValidHost ? apiHost : '',
    contextRoot,
    countryCode: !isHostnameContainsCC() ? countryCode : null,
    languageCode,
  });
}
